import { Injector, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth/auth.service';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { RefreshResult } from '../interfaces/user';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { AuthRefreshService } from '../services/auth/auth-refresh.service';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  private sessionExpiredNotified = false;

  constructor(
    private auth: AuthService,
    private router: Router,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('s3.amazonaws.com')) {
      return next.handle(request);
    }

    request = request.clone({
      url: this.setUrl(request.url),
    });
    request = this.addAuthorizationHeader(request);

    if (request.url.includes('auth/refresh')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {

        if (!navigator.onLine) {
          return throwError(() => error);
        }

        const tokens = this.auth.getTokens();

        if (error.status === 401 && tokens?.refreshToken) {
          const refreshToken = tokens.refreshToken;

          return this.authRefreshService.refreshTokens(refreshToken).pipe(
            catchError((refreshError: HttpErrorResponse) => {
              if (!this.isTransientRefreshError(refreshError)) {
                this.clearAuthAndRedirect();
              }

              return throwError(() => error);
            }),
            switchMap((response: RefreshResult) => {
              if (response.accessToken && response.refreshToken) {
                this.auth.saveTokens(response.accessToken, response.refreshToken);
                this.sessionExpiredNotified = false;

                return next.handle(this.addAuthorizationHeader(request));
              }

              if (!response.transient) {
                this.clearAuthAndRedirect();
              }

              return throwError(() => error);
            })
          );


        }

        return throwError(() => error);
      })
    );
  }

  private setUrl(url: string): string {
    if (url.startsWith('http')) {
      return url;
    }

    if (url.includes('/assets/locale/')) {
      return url;
    }

    return `${environment.apiUrl}${url}`;
  }

  private addAuthorizationHeader(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.auth.getTokens()?.token;

    if (token) {
      return request.clone({
        setHeaders: {
          authorization: `Bearer ${ token }`
        }
      });
    }

    return request;
  }

  private clearAuthAndRedirect(): void {
    if (!this.sessionExpiredNotified) {
      const notifier = this.injector.get(NotifierService);
      const translate = this.injector.get(TranslateService);

      notifier.notify('error', translate.instant('login.messages.sessionExpired'));
      this.sessionExpiredNotified = true;
    }

    this.auth.clearTokens();
    this.router.navigate(['/login']);
  }

  private isTransientRefreshError(error: HttpErrorResponse): boolean {
    return error.status === 0 || error.status >= 500;
  }

  private get authRefreshService(): AuthRefreshService {
    return this.injector.get(AuthRefreshService);
  }

}
